import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config/config'

export const UserContext = createContext();

export function UserProvider(props) {

  const [allUsers, setAllUsers] = useState([])
  const [userCount, setUserCount] = useState(0)
  const [page, setPage] = useState(1)
  const [unviewedUsers, setUnviewedUsers] = useState([])

  const [operatorUsers, setOperatorUsers] = useState([])

  // Load viewed users from localStorage on component mount
  useEffect(() => {
    const viewedUsers = localStorage.getItem('viewedUsers');
    if (viewedUsers) {
      const viewedIds = JSON.parse(viewedUsers);
      updateUnviewedUsers(viewedIds);
    }
  }, []);

  // Update unviewed users when allUsers change
  useEffect(() => {
    const viewedUsers = localStorage.getItem('viewedUsers');
    const viewedIds = viewedUsers ? JSON.parse(viewedUsers) : [];
    updateUnviewedUsers(viewedIds);
  }, [allUsers]);

  const updateUnviewedUsers = (viewedIds) => {
    const unviewed = allUsers.filter(user => !viewedIds.includes(user._id));
    setUnviewedUsers(unviewed);
  };

  const getAllUsers = useCallback(async (page, search, size) => {
    try {
      // ponytail: user paginated route has no `all` branch (unlike listings); 10000 is the same high-limit used by getHospitals pickers. Ceiling: users beyond 10000 are omitted; upgrade is a server `count === 'all'` find-all.
      const fetchAll = size === 'all'
      const response = await axios.put(`${config.apiUrl}/api/user/paginated/${page}/${fetchAll ? 10000 : 15}`, {
        search
      });
      if (!fetchAll) setAllUsers(response.data);
      return response.data;
    } catch (err) {
      console.log(err);
    }
  }, [])

  const getUserCount = async (search) => {
    try {
        const response = await axios.put(`${config.apiUrl}/api/user/count`,{
          search: search
        });
        setUserCount(response.data);
    } catch (err) {
      console.log(err);
    }
  }

  const markUserAsViewed = (userId) => {
    const viewedUsers = localStorage.getItem('viewedUsers');
    const viewedIds = viewedUsers ? JSON.parse(viewedUsers) : [];
    
    if (!viewedIds.includes(userId)) {
      const updatedViewedIds = [...viewedIds, userId];
      localStorage.setItem('viewedUsers', JSON.stringify(updatedViewedIds));
      updateUnviewedUsers(updatedViewedIds);
    }
  };

  const markAllUsersAsViewed = () => {
    const allUserIds = allUsers.map(user => user._id);
    localStorage.setItem('viewedUsers', JSON.stringify(allUserIds));
    setUnviewedUsers([]);
  };

  const clearViewedUsers = () => {
    localStorage.removeItem('viewedUsers');
    setUnviewedUsers(allUsers);
  };


  // Server role enum is Owner/Admin/Manager/User; CMS labels Manager as Listing Admin (facility admin alias kept).
  const toUserWritePayload = (data) => {
    const payload = { ...data }
    if (payload.organization_id) payload.organization_id = String(payload.organization_id)
    if (payload.role && ['listing admin', 'facility admin'].includes(String(payload.role).toLowerCase())) payload.role = 'Manager'
    if (Array.isArray(payload.remove_organization_ids)) {
      payload.remove_organization_ids = payload.remove_organization_ids.map(id => String(id))
    }
    return payload
  }

  const createUser = async (data, search) => {
    try {
      const response = await axios.post(`${config.apiUrl}/api/user/register-cms`, { data: toUserWritePayload(data) });
      getAllUsers(page, search)
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  // `additionalData` carries top-level preflight flags (keep_organization,
  // move_and_delete_organization) that are not user fields and must not go inside `data`.
  const editUser = async (id, data, search, additionalData = {}) => {
    try {
      const response = await axios.put(`${config.apiUrl}/api/user/user/${id}`, {
        ...additionalData,
        data: toUserWritePayload(data),
      });
      getAllUsers(page, search)
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const editUserPassword = async (id, data, search) => {
    try {
      const response = await axios.put(`${config.apiUrl}/api/user/user/password/${id}`, {data: data});
      getAllUsers(page, search)
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const deleteUser = async (id, search, additionalData = {}) => {
    try {
      const response = await axios.post(`${config.apiUrl}/api/user/user/delete/${id}`, additionalData);
      getAllUsers(page, search)
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }



  const getOperatorUsers = async (id) => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/user/users/${id}`);
      setOperatorUsers(response.data)
    } catch (err) {
      console.log(err);
    }
  }

  const createOperatorUser = async (data) => {
    try {
      const payload = toUserWritePayload(data)
      await axios.post(`${config.apiUrl}/api/user/register-cms`, { data: payload });
      getOperatorUsers(payload.organization_id)
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  
  const editOperatorUser = async (data, user_id, organization_id) => {
    try {
      const payload = toUserWritePayload({
        ...data,
        ...(organization_id ? { organization_id } : {}),
      })
      await axios.put(`${config.apiUrl}/api/user/user/${user_id}`, { data: payload });
      getOperatorUsers(organization_id)
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
  
  const deleteOperatorUser = async (user_id, organization_id, additionalData = {}) => {
    try {
      const response = await axios.post(`${config.apiUrl}/api/user/user/delete/${user_id}`, {
        ...additionalData,
        data: { organization_id: String(organization_id), ...additionalData.data },
      });
      getOperatorUsers(organization_id)
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const resetOperatorUserPassword = async (data, user_id) => {
    try {
      await axios.put(`${config.apiUrl}/api/user/user/password/${user_id}`, { data: data });
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  return (
    <UserContext.Provider
      value={{
        getAllUsers,
        allUsers,
        getUserCount,
        userCount,
        page, 
        setPage,
        deleteUser,
        createUser,
        editUser,
        editUserPassword,
        getOperatorUsers, 
        operatorUsers, 
        createOperatorUser, 
        editOperatorUser, 
        resetOperatorUserPassword, 
        deleteOperatorUser,
        unviewedUsers,
        markUserAsViewed,
        markAllUsersAsViewed,
        clearViewedUsers
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
}
